import { useContext, useMemo, useState } from "react";

import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack } from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { Roles } from "api/schemas/shared";
import { useGetTreatmentAdherence } from "api/services/patient/hooks/useGetTreatmentAdherence";
import { useProfileContext } from "contexts/ProfileContext/useProfileContext";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout/components/GraphSkeleton";
import { WidgetBox } from "shared/containers/WidgetBox";
import { CurrentPatientContext } from "utils/contexts";
import { userIsPartner } from "utils/profile/profileHelper";

import { BarChart } from "./BarChart";
import type { AdherenceView } from "./useFilterOptions";
import { useFilterOptions } from "./useFilterOptions";
import { WidgetBoxToolbar } from "./WidgetBoxToolbar";

export const AdherenceChart: React.FC = () => {
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  const { patient, previousTreatmentId } = useContext(CurrentPatientContext);
  const { defaultView, weekOptions, startTimes, endTime, barCounts } = useFilterOptions();
  const [view, setView] = useState<AdherenceView>(defaultView);
  const [filterEmptyDays, setFilterEmptyDays] = useState(false);

  invariant(profile, "Profile must be defined");

  const treatmentID = useMemo(() => {
    if (previousTreatmentId) {
      return previousTreatmentId;
    }

    // TODO: Fix the usage of any when patient context is refactored and typed
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const currentTreatment = patient.treatments.find((treatment: any) => treatment.is_current_treatment);
    return currentTreatment.id;
  }, [patient, previousTreatmentId]);

  const startTime = startTimes[view];

  const { data: adherenceData, isLoading } = useGetTreatmentAdherence({
    patient_id: patient.id,
    role: userIsPartner(profile) ? Roles.ClinicPartner : Roles.Therapist,
    treatment_id: treatmentID as number,
    params: { start_time: startTime, end_time: endTime },
  });
  const adherence = adherenceData?.adherence || {};

  const chartData = useMemo(() => {
    if (!adherence) return [];

    const entries = Object.entries(adherence).map(([date, values]) => ({
      date,
      adherence: values.adherence,
      completed: values.completed,
      total: values.total,
    }));
    return filterEmptyDays ? entries.filter(entry => entry.total > 0) : entries;
  }, [adherence, filterEmptyDays]);

  const adherenceStats = useMemo(() => {
    const noData = { adherencePercentage: null, adherenceDiff: null };
    if (!adherence) {
      return noData;
    }

    // Sort entries by date (ISO strings sort correctly lexicographically)
    const entries = Object.entries(adherence).sort(([a], [b]) => a.localeCompare(b));
    if (entries.length === 0) {
      return noData;
    }

    const filtered = entries.filter(([, v]) => v.total > 0);
    if (filtered.length === 0) return noData;
    const totalCompleted = filtered.reduce((sum, [, v]) => sum + v.completed, 0);
    const totalExercises = filtered.reduce((sum, [, v]) => sum + v.total, 0);
    const adherencePercentage = (totalCompleted / totalExercises) * 100;
    // Trend: difference between last and first date in the period
    const adherenceDiff = filtered[filtered.length - 1][1].adherence - filtered[0][1].adherence;

    return { adherencePercentage, adherenceDiff };
  }, [adherence]);

  return (
    <WidgetBox
      icon={<FontAwesomeIcon icon={faUserPlus} />}
      title={t("patients.adherence_graph.adherence")}
      color="green"
      toolbar={
        <WidgetBoxToolbar
          adherencePercentage={adherenceStats.adherencePercentage}
          adherenceDiff={adherenceStats.adherenceDiff}
          filterEmptyDays={filterEmptyDays}
          setFilterEmptyDays={setFilterEmptyDays}
        />
      }
      bordered
    >
      <Stack spacing={2} sx={{ alignItems: "center" }}>
        {isLoading && <GraphSkeleton height={278} barCount={barCounts[view]} />}
        {!isLoading && (
          <BarChart
            color="green"
            data={chartData}
            name={t("patients.adherence_graph.adherence")}
            xAxis={{ dataKey: "date", tickFormatter: value => format(parseISO(value), t("common.date.noYear")) }}
            yAxis={{ dataKey: "adherence" }}
            tooltipProps={{
              extraRows: [
                { dataKey: "completed", name: t("patients.adherence_graph.completed") },
                { dataKey: "total", name: t("patients.adherence_graph.total") },
              ],
              labelFormatter: label => format(parseISO(label), `${t("common.date.noYear")} yyyy`),
            }}
          />
        )}
        <ButtonsGroup group={weekOptions} active={view} setActive={value => setView(value as AdherenceView)} />
      </Stack>
    </WidgetBox>
  );
};
